import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PrivateRoutes from './routes/PrivateRoutes';
import EncuestaRoutes from './routes/EncuestaRoutes';
import ListadoDespachosAccordion from './pages/Despachos/despachosMobilePublic';
import OrdenCompraAprobarPublic from './pages/Compras/Ordenes/OrdenCompraAprobarPublic';
import Login from './components/Login';
import Logout from './components/Logout';
import Cookies from 'js-cookie';
import ProductSearch from './pages/Productos/BuscarProducto/ProductBuscarDesktop';
import MonitorVentas from './pages/Monitor/MonitorVentas';
import { PermissionsProvider } from './context/PermissionsContext';
import AccessDenied from './pages/AccessDenied';

// 1. Importaciones para Socket.IO y React Toastify
import io from 'socket.io-client';
import { ToastContainer } from 'react-toastify';
import handleNotification from './utils/handleNotification';
import 'react-toastify/dist/ReactToastify.css';
import { playAudioNotification, initializeAudioPermission } from './utils/audioNotifications';
import {
    isPublicAuthRoute,
    shouldShowVisualToast,
    shouldPlayAudioForKey,
} from './utils/notificationPrefs';

// 2. Conexión al servidor de Socket.IO
// Configurar axios para enviar credenciales (cookies)
axios.defaults.withCredentials = true;

export const socket = io(`${window.location.protocol}//${window.location.hostname}:${window.location.port || 443}`, {
    withCredentials: true,
    autoConnect: false, // No conectar hasta que el usuario esté autenticado
});


// Componente de restricción por IP (ipify). Espera a que allowedIP esté disponible.
const RestrictedRoute = ({ allowedIP, children, allowInDev = false }) => {
    const [isAllowed, setIsAllowed] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (allowInDev && process.env.NODE_ENV === 'development') {
            setIsAllowed(true);
            setLoading(false);
            return;
        }

        // Esperar config dinámica / env antes de decidir denegar
        if (!allowedIP) {
            setIsAllowed(false);
            setLoading(true);
            return;
        }

        let cancelled = false;

        const fetchIP = async () => {
            setLoading(true);
            try {
                const response = await fetch('https://api.ipify.org?format=json');
                const data = await response.json();
                if (!cancelled) {
                    setIsAllowed(data.ip === allowedIP);
                }
            } catch (error) {
                console.error('Error al obtener la IP:', error);
                if (!cancelled) setIsAllowed(false);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchIP();

        return () => {
            cancelled = true;
        };
    }, [allowedIP, allowInDev]);

    if (loading) return <div>Cargando...</div>;
    if (!isAllowed) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8d7da', color: '#721c24', fontSize: '24px', fontWeight: 'bold', textAlign: 'center' }}>Acceso Restringido</div>;
    return children;
};

// Componente principal de la aplicación
const AppContent = () => {
    const [token, setToken] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(undefined);
    const [userRole, setUserRole] = useState(null); // Nuevo estado para el rol
    const [dynamicIPAlmacen, setDynamicIPAlmacen] = useState(null);
    const location = useLocation();

    useEffect(() => {
        // Definimos los eventos de interacción de usuario
        const INTERACTION_EVENTS = ['click', 'keydown', 'touchstart', 'touchend'];

        const handleUserInteraction = () => {
            console.log('[App] Interacción del usuario detectada. Inicializando permiso de audio...');
            initializeAudioPermission();

            // Eliminamos todos los listeners después del primer éxito
            INTERACTION_EVENTS.forEach(event => {
                document.removeEventListener(event, handleUserInteraction);
            });
        };

        // Adjuntar listeners de eventos con 'once: true'
        INTERACTION_EVENTS.forEach(event => {
            document.addEventListener(event, handleUserInteraction, { once: true });
        });

        // Función de limpieza
        return () => {
            INTERACTION_EVENTS.forEach(event => {
                document.removeEventListener(event, handleUserInteraction);
            });
        };
    }, []);

    // Conectar socket solo en rutas privadas autenticadas (nunca en login/logout)
    useEffect(() => {
        const onPublicRoute = isPublicAuthRoute(location.pathname);
        const shouldConnect = isTokenValid === true && !onPublicRoute;

        const handleConnectError = (err) => {
            if (err?.message === 'UNAUTHORIZED' || err?.message === 'SESSION_REVOKED') {
                if (socket.connected) socket.disconnect();
            }
        };

        socket.on('connect_error', handleConnectError);

        if (shouldConnect) {
            if (!socket.connected) socket.connect();
        } else if (isTokenValid === false || onPublicRoute) {
            if (socket.connected) socket.disconnect();
        }

        return () => {
            socket.off('connect_error', handleConnectError);
        };
    }, [isTokenValid, location.pathname]);


    // Interceptor global para manejar errores de sesión revocada
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    // Si el backend nos dice explícitamente que la sesión fue revocada
                    if (error.response.data && error.response.data.code === 'SESSION_REVOKED') {
                        console.warn('Sesión revocada detectada. Cerrando sesión...');

                        setIsTokenValid(false);
                        setToken('');
                        Cookies.remove('token');

                        // Opcional: Mostrar notificación al usuario antes de redirigir (si usas toast)
                        // toast.error('Tu sesión ha expirado o se ha iniciado sesión en otro dispositivo.');
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []);
    // --- Notification preference state (for audio filtering) ---
    const [userNotifPrefs, setUserNotifPrefs] = useState(null);
    const [globalNotifConfig, setGlobalNotifConfig] = useState(null);

    // Fetch user notification preferences after auth
    useEffect(() => {
        if (!isTokenValid) return;
        const fetchNotifPrefs = async () => {
            try {
                const [prefsRes, configRes] = await Promise.all([
                    axios.get('/api/notifications/preferences'),
                    axios.get('/api/notifications/settings'),
                ]);
                setUserNotifPrefs(prefsRes.data);
                setGlobalNotifConfig(configRes.data);
            } catch (err) {
                console.error('Error fetching notification prefs:', err);
            }
        };
        fetchNotifPrefs();

        // Listen for realtime preference updates from NotificationSettings
        window.addEventListener('userPreferencesUpdated', fetchNotifPrefs);
        return () => {
            window.removeEventListener('userPreferencesUpdated', fetchNotifPrefs);
        };
    }, [isTokenValid]);

    // Toast notification listeners (visual popups only — style === 'toast')
    useEffect(() => {
        const excludedRoutes = ['/encuesta', '/login', '/logout', '/monitor', '/compras/aprobar'];
        const isExcludedRoute = excludedRoutes.some((route) => location.pathname.startsWith(route));

        if (isExcludedRoute || !isTokenValid || !userNotifPrefs) {
            return;
        }

        const handleSocketNotification = (data) => {
            if (!shouldShowVisualToast(userNotifPrefs, data)) {
                return;
            }
            handleNotification(data);
        };

        socket.on('info', handleSocketNotification);
        socket.on('meli', handleSocketNotification);
        socket.on('nueva-venta', handleSocketNotification);
        socket.on('notification_new_message', handleSocketNotification);
        socket.on('meli:new_message', handleSocketNotification);
        socket.on('nuevo-pedido-ecommerce', handleSocketNotification);
        socket.on('ecommerce_admin_alert', handleSocketNotification);

        socket.on('connect', () => {
            console.log('Conectado al servidor de sockets');
        });

        return () => {
            socket.off('info', handleSocketNotification);
            socket.off('meli', handleSocketNotification);
            socket.off('nueva-venta', handleSocketNotification);
            socket.off('notification_new_message', handleSocketNotification);
            socket.off('meli:new_message', handleSocketNotification);
            socket.off('nuevo-pedido-ecommerce', handleSocketNotification);
            socket.off('ecommerce_admin_alert', handleSocketNotification);
        };
    }, [location.pathname, isTokenValid, userNotifPrefs]);

    // Audio notification listener (with per-user pref filtering)
    useEffect(() => {
        const excludedRoutes = ['/encuesta', '/login', '/logout', '/monitor', '/compras/aprobar'];
        const isExcludedRoute = excludedRoutes.some((route) => location.pathname.startsWith(route));

        if (isExcludedRoute || !isTokenValid || !userNotifPrefs) {
            return;
        }

        const handleAudioNotification = (audioKey) => {
            if (!shouldPlayAudioForKey(userNotifPrefs, globalNotifConfig, audioKey)) {
                return;
            }
            playAudioNotification(audioKey);
        };

        socket.on('audio_notification', handleAudioNotification);

        return () => {
            socket.off('audio_notification', handleAudioNotification);
        };
    }, [location.pathname, isTokenValid, userNotifPrefs, globalNotifConfig]);

    // Lógica de autenticación (actualizada para HttpOnly cookies)
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await axios.get('/api/auth/config');
                if (response.data.ipAlmacen) {
                    setDynamicIPAlmacen(response.data.ipAlmacen);
                }
            } catch (err) {
                console.error('Error fetching dynamic app config:', err);
            }
        };

        fetchConfig();
    }, []);

    useEffect(() => {
        const checkSession = async () => {
            if (location.pathname === '/login') {
                // Si estamos en login, no verificamos sesión automáticamente para evitar redirigir si el usuario quiere loguearse con otra cuenta,
                // aunque esto podría ser debatible. Por ahora, dejamos que Login maneje su estado.
                // Sin embargo, si ya hay cookie, Login.js podría redirigir.
                // Mejor: Verificamos siempre, y Login redirige si ya está authed.
            }

            try {
                // Verificar sesión con el backend
                const response = await axios.get('/api/auth/check-session');
                if (response.data.authenticated) {
                    setToken('valid-session'); // Dummy token para que PrivateRoutes no falle
                    // Guardamos los permisos en userRole para inicializar el PermissionsContext
                    if (response.data.permissions) {
                        setUserRole(response.data.permissions);
                    } else if (response.data.user && response.data.user.id_rol) {
                        setUserRole(response.data.user.id_rol);
                    }
                    setIsTokenValid(true);
                } else {
                    throw new Error('No authenticated');
                }
            } catch (error) {
                console.log('Sesión no válida o expirada');
                setIsTokenValid(false);
                Cookies.remove('token'); // Limpiar por si acaso queda algo viejo
                Cookies.remove('tokenExpiration');
            }
        };

        if (location.pathname === '/login' || location.pathname === '/logout') {
            setIsTokenValid(false);
            setToken('');
            setUserRole(null);
            setUserNotifPrefs(null);
            setGlobalNotifConfig(null);
        } else if (
            location.pathname.startsWith('/monitor')
            || location.pathname.startsWith('/compras/aprobar')
        ) {
            // Rutas públicas: no guardar redirect ni exigir sesión
            checkSession();
        } else {
            Cookies.set('redirectPath', location.pathname, { expires: 1 });
            checkSession();
        }

    }, [location.pathname]);

    if (isTokenValid === undefined) {
        return <div>Cargando...</div>;
    }

    const isMonitorRoute = location.pathname.startsWith('/monitor');

    return (
        <PermissionsProvider initialPermissions={userRole}>
            {/* 4. Contenedor para que se muestren las notificaciones */}
            {!isMonitorRoute && (
                <ToastContainer
                    position="bottom-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                    toastStyle={{ marginBottom: '20px' }}
                    style={{ zIndex: 2147483647 }} // Máximo z-index posible para estar sobre cualquier elemento
                />
            )}
            <Routes>
                <Route path="/login" element={<Login setToken={setToken} />} />
                <Route
                    path="/monitor/ventas"
                    element={<MonitorVentas />}
                />
                <Route path="/encuesta/*" element={<EncuestaRoutes />} />
                <Route
                    path="restricted/productos/buscar"
                    element={<RestrictedRoute allowedIP={dynamicIPAlmacen || process.env.REACT_APP_IP_ALMACEN}><ProductSearch /></RestrictedRoute>}
                />
                <Route path="/access-denied" element={<AccessDenied />} />
                <Route path="/logout" element={<Logout setToken={setToken} />} />
                <Route path="/despachos" element={<ListadoDespachosAccordion />} />
                <Route path="/compras/aprobar" element={<OrdenCompraAprobarPublic />} />
                <Route path="/" element={isTokenValid ? <Navigate to="/dashboard/welcome" /> : <Navigate to="/login" />} />
                <Route path="/*" element={isTokenValid ? <PrivateRoutes token={token} splitUserRole={userRole} /> : <Navigate to="/login" />} />
            </Routes>
        </PermissionsProvider>
    );
};

// Componente principal que exporta la aplicación
const App = () => {
    return <AppContent />;
};

export default App;
